import React, { useState, useEffect, useRef, useCallback } from 'react';
import { TwitchGame, GameFilter } from '../types';
import { getTopGames, searchCategories } from '../services/twitchApi';
import { boxArtUrl } from '../utils/appRoutes';
import { IconCrosshair } from './icons';
import './GamePicker.css';

interface GamePickerProps {
  gameFilter: GameFilter | null;
  onChange: (game: GameFilter | null) => void;
}

const toFilter = (game: TwitchGame): GameFilter => ({
  id: game.id,
  name: game.name,
  boxArt: game.box_art_url,
});

// Top categories barely move — fetch once per page load, share across opens
let topGamesCache: TwitchGame[] | null = null;

/**
 * Category lock for the Top tab. Idle it reads "All categories"; armed it
 * shows the game's box art and name, and the whole top-100 pool — ranking,
 * heat, auto-switch — runs inside that category.
 */
export const GamePicker: React.FC<GamePickerProps> = ({ gameFilter, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<TwitchGame[]>([]);
  const [topGames, setTopGames] = useState<TwitchGame[]>(topGamesCache || []);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const queryRef = useRef('');

  // Load the top-categories grid the first time the panel opens
  useEffect(() => {
    if (!open || topGamesCache) return;
    getTopGames().then(games => {
      if (games.length > 0) {
        topGamesCache = games;
        setTopGames(games);
      }
    });
  }, [open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
    if (!open) {
      setQuery('');
      queryRef.current = '';
      setResults([]);
    }
  }, [open]);

  // Debounced category search
  const handleQuery = useCallback((value: string) => {
    setQuery(value);
    queryRef.current = value;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value.trim()) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      const found = await searchCategories(value);
      // Ignore stale responses after the query moved on
      if (queryRef.current === value) {
        setResults(found);
        setSearching(false);
      }
    }, 250);
  }, []);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const pick = (game: TwitchGame) => {
    onChange(toFilter(game));
    setOpen(false);
  };

  const showResults = query.trim().length > 0;

  return (
    <div className="game-picker">
      <div className="sector-row">
        <button
          className={`sector-btn ${gameFilter ? 'armed' : ''}`}
          onClick={() => setOpen(o => !o)}
          aria-expanded={open}
          title={gameFilter
            ? `Top 100 locked to ${gameFilter.name} — click to change`
            : 'Lock the Top 100 to one category'}
        >
          {gameFilter?.boxArt ? (
            <img className="sector-art" src={boxArtUrl(gameFilter.boxArt, 57, 76)} alt="" />
          ) : (
            <IconCrosshair size={13} className="sector-ico" />
          )}
          <span className="sector-name">{gameFilter ? gameFilter.name : 'All categories'}</span>
          <svg
            className={`sector-caret ${open ? 'up' : ''}`}
            width="10" height="10" viewBox="0 0 16 16" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            aria-hidden
          >
            <path d="m3 6 5 5 5-5" />
          </svg>
        </button>
        {gameFilter && (
          <button
            className="sector-clear"
            onClick={() => { onChange(null); setOpen(false); }}
            title="Back to all categories"
            aria-label={`Stop filtering by ${gameFilter.name}`}
          >
            ×
          </button>
        )}
      </div>

      {open && (
        <div className="sector-panel">
          <input
            ref={inputRef}
            type="text"
            className="sector-search"
            placeholder="Search categories"
            value={query}
            onChange={e => handleQuery(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Escape') setOpen(false);
              if (e.key === 'Enter' && results.length > 0) pick(results[0]);
            }}
          />
          {showResults ? (
            <div className="sector-results">
              {searching && results.length === 0 && (
                <div className="sector-note">searching…</div>
              )}
              {!searching && results.length === 0 && (
                <div className="sector-note">No categories match “{query.trim()}”.</div>
              )}
              {results.map(game => (
                <button key={game.id} className="sector-result" onClick={() => pick(game)}>
                  <img src={boxArtUrl(game.box_art_url, 57, 76)} alt="" loading="lazy" />
                  <span>{game.name}</span>
                </button>
              ))}
            </div>
          ) : (
            <>
              <div className="sector-grid-label">Top right now</div>
              <div className="sector-grid">
                {topGames.length === 0 && <div className="sector-note">loading…</div>}
                {topGames.slice(0, 12).map(game => (
                  <button
                    key={game.id}
                    className={`sector-cell ${gameFilter?.id === game.id ? 'active' : ''}`}
                    onClick={() => pick(game)}
                    title={game.name}
                  >
                    <img src={boxArtUrl(game.box_art_url, 285, 380)} alt="" loading="lazy" />
                    <span>{game.name}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
